<template>
  <section
    class="dashboard-agenda-hub dashboard-upcoming"
    aria-labelledby="dashboard-agenda-hub-title"
  >
    <div class="dashboard-agenda-hub__header">
      <h2 id="dashboard-agenda-hub-title" class="dashboard-upcoming__title">
        {{ $t("dashboard_my_agenda") }}
      </h2>
      <span class="dashboard-agenda-hub__period">{{ periodLabel }}</span>
    </div>

    <div class="uni-filters" role="tablist">
      <button
        v-for="chip in filterChips"
        :key="chip.value"
        v-tooltip.top="chip.label"
        type="button"
        class="uni-chip"
        :class="[
          `uni-chip--${chip.value}`,
          { 'uni-chip--active': filter === chip.value },
        ]"
        :aria-pressed="filter === chip.value"
        @click="filter = chip.value"
      >
        <i :class="chip.icon" class="mr-1" aria-hidden="true" />

        <span class="uni-chip__count">{{ chip.count }}</span>
      </button>
    </div>

    <ProgressBar
      v-if="isLoading && !hasAnyItem"
      mode="indeterminate"
      class="dashboard-upcoming__loading"
    />

    <div
      v-else-if="!dayGroups.length"
      class="dashboard-upcoming__empty dashboard-agenda-hub__empty"
    >
      <i
        class="pi pi-calendar dashboard-upcoming__empty-icon"
        aria-hidden="true"
      />
      <p class="dashboard-upcoming__empty-text">{{ emptyMessage }}</p>
      <router-link
        :to="{ name: 'appointments-new' }"
        class="dashboard-upcoming__link"
      >
        {{ $t("dashboard_agenda_schedule_now") }}
        <i class="pi pi-angle-right" aria-hidden="true" />
      </router-link>
    </div>

    <div v-else class="uni-timeline">
      <div
        v-for="group in dayGroups"
        :key="group.dateKey"
        class="uni-day"
        :class="{ 'uni-day--today': group.isToday }"
      >
        <div class="uni-day__date">
          <span class="uni-day__num">{{ group.dayNumber }}</span>
          <span class="uni-day__month">{{ group.monthLabel }}</span>
          <span class="uni-day__weekday">{{ group.weekdayLabel }}</span>
        </div>
        <div class="uni-day__rail">
          <span
            class="uni-day__dot"
            :class="`uni-day__dot--${group.dotVariant}`"
            aria-hidden="true"
          />
          <div class="uni-day__line" />
        </div>
        <div class="uni-day__cards">
          <article
            v-for="item in group.items"
            :key="item.id"
            class="uni-card"
            :class="cardClasses(item)"
            @click="onCardClick(item)"
          >
            <!-- Agendamento -->
            <template v-if="item.kind === 'appointment'">
              <div class="uni-card__head">
                <div class="uni-card__head-left">
                  <span class="uni-card__time uni-card__time--state">
                    {{ formatAppointmentTime(item.data) }}
                  </span>
                  <span class="origin-tag origin-tag--appointment">
                    <i class="pi pi-calendar mr-1" />
                    {{ $t("appointments") }}
                  </span>
                </div>
                <i class="pi pi-angle-right uni-card__open" />
              </div>
              <h3 class="uni-card__title">
                {{ item.data.servicePriceName || item.data.title || "Serviço" }}
              </h3>
              <div class="uni-card__meta">
                <span
                  class="uni-card__status uni-card__status--state"
                  :class="`uni-card__status--${appointmentStateClass(
                    item.data.state
                  )}`"
                >
                  {{ appointmentStatusLabel(item.data.state) }}
                </span>
                <span
                  v-if="item.data.consultantName"
                  class="uni-card__meta-text"
                >
                  <i class="pi pi-user mr-1" />
                  {{ item.data.consultantName }}
                </span>
                <span v-if="item.data.officeName" class="uni-card__meta-text">
                  <i class="pi pi-map-marker mr-1" />
                  {{ item.data.officeName }}
                </span>
              </div>
            </template>

            <!-- Webinar / evento público -->
            <template v-else>
              <div class="uni-card__head">
                <div class="uni-card__head-left">
                  <span
                    v-if="formatEventTimeOnly(item.data) !== '—'"
                    class="uni-card__time"
                  >
                    {{ formatEventTimeOnly(item.data) }}
                  </span>
                  <span
                    class="origin-tag"
                    :class="`origin-tag--${item.data.type || 'public'}`"
                  >
                    {{ getEventTypeLabel(item.data.type) }}
                  </span>
                  <a
                    v-if="item.data.link"
                    :href="item.data.link"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="uni-external-link"
                    @click.stop
                  >
                    <i class="pi pi-external-link" />
                  </a>
                </div>
                <div
                  v-if="showWebinarActions(item.data)"
                  class="uni-card__actions"
                  @click.stop
                >
                  <Button
                    v-if="item.data.isRegistered"
                    :label="$t('webinar_cancel_registration')"
                    class="p-button-sm p-button-text uni-card__cancel-btn"
                    :loading="cancellingWebinarId === item.data.id"
                    @click="cancelWebinarRegistration(item.data)"
                  />
                  <Button
                    v-else-if="canRegisterWebinar(item.data)"
                    :label="$t('webinar_register')"
                    class="p-button-sm uni-card__register-btn"
                    :loading="registeringWebinarId === item.data.id"
                    @click="registerWebinar(item.data)"
                  />
                </div>
                <div
                  v-else-if="showConferenceActions(item.data)"
                  class="uni-card__actions"
                  @click.stop
                >
                  <Button
                    v-if="item.data.isRegistered"
                    :label="$t('conference_registered_label')"
                    class="p-button-sm p-button-outlined"
                    disabled
                  />
                  <Button
                    v-else-if="item.data.soldOut"
                    :label="$t('conference_sold_out')"
                    class="p-button-sm p-button-outlined p-button-secondary"
                    disabled
                  />
                  <Button
                    v-else-if="item.data.canRegister"
                    :label="$t('conference_pay')"
                    class="p-button-sm uni-card__register-btn"
                    :disabled="!item.data.link"
                    @click="openLanding(item.data)"
                  />
                  <p
                    v-if="item.data.canRegister && !item.data.link"
                    class="uni-card__meta-text"
                  >
                    {{ $t("conference_landing_missing") }}
                  </p>
                </div>
              </div>
              <h3 class="uni-card__title">{{ item.data.title }}</h3>
              <div class="uni-card__meta">
                <span
                  v-if="getEventDaysLabel(item.data)"
                  class="uni-card__status uni-card__status--upcoming"
                >
                  {{ getEventDaysLabel(item.data) }}
                </span>
                <span v-if="item.data.authors" class="uni-card__meta-text">
                  {{ item.data.authors }}
                </span>
                <span
                  v-if="item.data.location || item.data.room"
                  class="uni-card__meta-text"
                >
                  <i class="pi pi-map-marker mr-1" />
                  {{
                    [item.data.location, item.data.room]
                      .filter(Boolean)
                      .join(" · ")
                  }}
                </span>
                <span
                  v-if="
                    item.data.type === 'conference' && formatPrice(item.data)
                  "
                  class="uni-card__meta-text"
                >
                  {{ formatPrice(item.data) }}
                </span>
              </div>
            </template>
          </article>
        </div>
      </div>
    </div>

    <div
      v-if="hasAnyItem"
      class="dashboard-upcoming__actions dashboard-agenda-hub__footer"
    >
      <router-link
        :to="{ name: 'appointments' }"
        class="dashboard-upcoming__link"
      >
        {{ $t("dashboard_agenda_all_appointments") }}
        <i class="pi pi-angle-right" aria-hidden="true" />
      </router-link>
      <router-link
        :to="{ name: 'eventos' }"
        class="dashboard-upcoming__link"
      >
        {{ $t("events") }}
        <i class="pi pi-angle-right" aria-hidden="true" />
      </router-link>
      <router-link
        v-if="hasBalcaoMais"
        :to="{ name: 'webinars' }"
        class="dashboard-upcoming__link"
      >
        {{ $t("webinars") }}
        <i class="pi pi-angle-right" aria-hidden="true" />
      </router-link>
    </div>
  </section>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Button from "primevue/button";
import ProgressBar from "primevue/progressbar";
import {
  appointmentStateClass,
  appointmentStatusLabel,
  buildDashboardDateRange,
  buildUnifiedDayGroups,
  formatAppointmentTime,
  formatEventTimeOnly,
  formatShortPeriodLabel,
  getEventDaysLabel,
  getEventTypeLabel,
  getAppointmentStartDate,
  isDateWithinRange,
  isUpcomingPublicEvent,
  isUpcomingWebinar,
  mapAppointmentsToItems,
  mapEventsToItems,
} from "@/utils/dashboard/unifiedTimelineShared";

dayjs.extend(utc);

export default {
  name: "DashboardAgendaHub",
  components: { Button, ProgressBar },
  props: {
    hasBalcaoMais: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const range = buildDashboardDateRange();
    return {
      filter: "all",
      isLoadingAppointments: false,
      isLoadingWebinars: false,
      isLoadingEvents: false,
      appointments: [],
      webinars: [],
      publicEvents: [],
      registeringWebinarId: null,
      cancellingWebinarId: null,
      rangeStart: range.start,
      rangeEnd: range.end,
    };
  },
  computed: {
    periodLabel() {
      return formatShortPeriodLabel(this.rangeStart, this.rangeEnd);
    },
    isLoading() {
      return (
        this.isLoadingAppointments ||
        this.isLoadingWebinars ||
        this.isLoadingEvents
      );
    },
    appointmentItems() {
      return mapAppointmentsToItems(this.upcomingAppointments).filter((item) =>
        isDateWithinRange(item.date, this.rangeStart, this.rangeEnd)
      );
    },
    webinarItems() {
      if (!this.hasBalcaoMais) return [];
      return mapEventsToItems(this.upcomingWebinars).filter((item) =>
        isDateWithinRange(item.date, this.rangeStart, this.rangeEnd)
      );
    },
    publicEventItems() {
      return mapEventsToItems(this.upcomingPublicEvents).filter((item) =>
        isDateWithinRange(item.date, this.rangeStart, this.rangeEnd)
      );
    },
    allItems() {
      return [
        ...this.appointmentItems,
        ...this.webinarItems,
        ...this.publicEventItems,
      ];
    },
    hasAnyItem() {
      return this.allItems.length > 0;
    },
    dayGroups() {
      return buildUnifiedDayGroups(this.allItems, this.filter);
    },
    filterChips() {
      const chips = [
        {
          value: "all",
          label: this.$t("dashboard_agenda_filter_all"),
          icon: "pi pi-th-large",
          count: this.allItems.length,
        },
        {
          value: "appointment",
          label: this.$t("dashboard_agenda_filter_appointments"),
          icon: "pi pi-calendar",
          count: this.appointmentItems.length,
        },
      ];
      if (this.hasBalcaoMais) {
        chips.push({
          value: "webinar",
          label: this.$t("dashboard_agenda_filter_webinars"),
          icon: "pi pi-video",
          count: this.webinarItems.length,
        });
      }
      chips.push({
        value: "event",
        label: this.$t("dashboard_agenda_filter_events"),
        icon: "pi pi-ticket",
        count: this.publicEventItems.length,
      });
      return chips;
    },
    emptyMessage() {
      switch (this.filter) {
        case "appointment":
          return this.$t("dashboard_agenda_empty_appointments");
        case "webinar":
          return this.$t("no_upcoming_webinars");
        case "event":
          return this.$t("no_upcoming_events");
        default:
          return this.$t("dashboard_agenda_empty");
      }
    },
    upcomingAppointments() {
      const now = dayjs();
      return (this.appointments || []).filter((appointment) => {
        const start = getAppointmentStartDate(appointment);
        if (!start) return false;
        return dayjs.utc(start).local().isAfter(now);
      });
    },
    upcomingWebinars() {
      return (this.webinars || []).filter((webinar) =>
        isUpcomingWebinar(webinar)
      );
    },
    upcomingPublicEvents() {
      return (this.publicEvents || []).filter((event) =>
        isUpcomingPublicEvent(event)
      );
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    formatAppointmentTime,
    formatEventTimeOnly,
    getEventTypeLabel,
    getEventDaysLabel,
    appointmentStateClass,
    appointmentStatusLabel,
    cardClasses(item) {
      const classes = [`uni-card--${item.kind}`];
      if (item.kind === "appointment") {
        classes.push(
          `uni-card--appt-${appointmentStateClass(item.data.state)}`
        );
      }
      if (item.kind === "event") {
        if (item.data.type === "webinar") {
          classes.push("uni-card--event-webinar");
          if (item.data.isRegistered) {
            classes.push("uni-card--webinar-registered");
          }
        } else if (item.data.type === "conference") {
          classes.push("uni-card--event-conference");
          if (item.data.isRegistered) {
            classes.push("uni-card--conference-registered");
          }
        } else {
          classes.push("uni-card--event-public");
        }
      }
      return classes;
    },
    showWebinarActions(event) {
      return (
        event.type === "webinar" &&
        this.hasBalcaoMais &&
        (event.isRegistered || this.canRegisterWebinar(event))
      );
    },
    showConferenceActions(event) {
      return (
        event.type === "conference" &&
        (event.isRegistered || event.soldOut || event.canRegister)
      );
    },
    formatPrice(event) {
      if (!event || event.price == null || event.price === "") {
        return "";
      }
      const amount = Number(event.price);
      if (Number.isNaN(amount)) {
        return "";
      }
      const formatted = amount.toLocaleString("pt-PT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const currency =
        event.currency && event.currency !== "eur" ? event.currency : "€";
      return `${formatted} ${currency}`;
    },
    canRegisterWebinar(webinar) {
      if (typeof webinar.canRegister === "boolean") {
        return webinar.canRegister;
      }
      return isUpcomingWebinar(webinar) && !webinar.isRegistered;
    },
    onCardClick(item) {
      if (item.kind === "appointment") {
        this.$router.push({ name: "appointments" });
      }
    },
    loadAll() {
      this.fetchAppointments();
      if (this.hasBalcaoMais) {
        this.fetchWebinars();
      }
      this.fetchPublicEvents();
    },
    fetchAppointments() {
      this.isLoadingAppointments = true;
      this.$root
        .apiGet(this.$root.api.appointments, { params: { dataType: "list" } })
        .then((response) => {
          this.appointments = response.data.data || [];
        })
        .catch((error) => {
          this.$root.toastError(this.errorCode(error));
        })
        .finally(() => {
          this.isLoadingAppointments = false;
        });
    },
    fetchWebinars() {
      this.isLoadingWebinars = true;
      this.$root
        .apiGet(this.$root.api.profileWebinars, { params: { upcoming: true } })
        .then((response) => {
          this.webinars = response.data.data || [];
        })
        .catch((error) => {
          this.$root.toastError(this.errorCode(error));
        })
        .finally(() => {
          this.isLoadingWebinars = false;
        });
    },
    fetchPublicEvents() {
      this.isLoadingEvents = true;
      this.$root
        .apiGet(this.$root.api.profileEvents, { params: { upcoming: true } })
        .then((response) => {
          this.publicEvents = response.data.data || [];
        })
        .catch((error) => {
          this.$root.toastError(this.errorCode(error));
        })
        .finally(() => {
          this.isLoadingEvents = false;
        });
    },
    openLanding(event) {
      if (event && event.link) {
        window.open(event.link, "_blank", "noopener");
        return;
      }
      this.$root.toastError(this.$t("conference_landing_missing"));
    },
    registerWebinar(webinar) {
      this.registeringWebinarId = webinar.id;
      this.$root
        .apiPost(`${this.$root.api.profileWebinars}/${webinar.id}/register`)
        .then((response) => {
          this.$root.toastSuccess(this.$t(response.data.msg_code));
          this.fetchWebinars();
        })
        .catch((error) => {
          this.$root.toastError(this.errorCode(error));
        })
        .finally(() => {
          this.registeringWebinarId = null;
        });
    },
    cancelWebinarRegistration(webinar) {
      this.$confirm.require({
        group: "headless",
        header: this.$t("webinar_cancel_registration"),
        message: this.$t("webinar_cancel_registration_confirm"),
        rejectLabel: this.$t("bt_no"),
        acceptLabel: this.$t("bt_yes"),
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button p-component p-button-warning ml-2",
        rejectClass:
          "p-button p-component p-button-secondary p-button-outlined",
        accept: () => {
          this.submitWebinarCancellation(webinar);
        },
      });
    },
    submitWebinarCancellation(webinar) {
      this.cancellingWebinarId = webinar.id;
      this.$root
        .apiDelete(`${this.$root.api.profileWebinars}/${webinar.id}/register`)
        .then((response) => {
          const msgCode =
            response.data && response.data.msg_code
              ? response.data.msg_code
              : "webinar_registration_cancelled";
          this.$root.toastSuccess(this.$t(msgCode));
          this.fetchWebinars();
        })
        .catch((error) => {
          this.$root.toastError(this.errorCode(error));
        })
        .finally(() => {
          this.cancellingWebinarId = null;
        });
    },
    errorCode(error) {
      return (
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.msg_code) ||
        "error"
      );
    },
  },
};
</script>

<style scoped>
.dashboard-agenda-hub {
  --agenda-primary: #0c233f;
  --agenda-accent: #0f97c7;
  --agenda-accent-dark: #096d91;
  --agenda-accent-soft: rgba(15, 151, 199, 0.1);
  --agenda-success: #437c5e;
  --agenda-success-soft: rgba(67, 124, 94, 0.12);
  --agenda-warn: #b8860b;
  --agenda-warn-soft: rgba(184, 134, 11, 0.1);
  --agenda-muted: #64748b;
  --agenda-border: #e8ecf2;
  --agenda-surface: #f8fafc;
  --agenda-violet: #5b4d8a;
  --agenda-violet-soft: rgba(91, 77, 138, 0.1);

  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.dashboard-agenda-hub__header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.dashboard-agenda-hub__period {
  font-size: 0.8rem;
  color: var(--agenda-muted);
}

.uni-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
}

.uni-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  border: 1px solid var(--agenda-border);
  background: #fff;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--agenda-muted);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.uni-chip:hover {
  border-color: #c5d0dc;
  color: var(--agenda-primary);
}

.uni-chip--active {
  background: var(--agenda-primary);
  border-color: var(--agenda-primary);
  color: #fff;
}

.uni-chip--active .uni-chip__count {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.uni-chip__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: var(--agenda-surface);
  font-size: 0.7rem;
  color: var(--agenda-muted);
}

.uni-timeline {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.uni-day {
  display: grid;
  grid-template-columns: 3.5rem 1.25rem 1fr;
  gap: 0.65rem;
  align-items: start;
}

.uni-day__date {
  text-align: right;
  padding-top: 0.15rem;
}

.uni-day__num {
  display: block;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--agenda-primary);
}

.uni-day--today .uni-day__num {
  color: var(--agenda-accent-dark);
}

.uni-day__month,
.uni-day__weekday {
  display: block;
  font-size: 0.65rem;
  text-transform: uppercase;
  color: var(--agenda-muted);
  line-height: 1.2;
}

.uni-day__rail {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
}

.uni-day__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #c5d0dc;
  flex-shrink: 0;
  margin-top: 0.4rem;
  box-shadow: 0 0 0 3px #fff;
}

.uni-day__dot--today {
  background: var(--agenda-accent);
  box-shadow: 0 0 0 3px var(--agenda-accent-soft);
}

.uni-day__dot--upcoming {
  background: #d1dae6;
}

.uni-day__line {
  flex: 1;
  width: 2px;
  background: #eef1f6;
  margin-top: 0.25rem;
  min-height: 1rem;
}

.uni-day:last-child .uni-day__line {
  display: none;
}

.uni-card {
  border: 1px solid var(--agenda-border);
  border-radius: 10px;
  padding: 0.8rem 0.95rem;
  margin-bottom: 0.5rem;
  background: #fff;
  box-shadow: 0 1px 2px rgba(12, 35, 63, 0.04);
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.uni-card:last-child {
  margin-bottom: 0;
}

.uni-card--appointment {
  cursor: pointer;
  border-left: 3px solid transparent;
}

.uni-card--appointment:hover {
  box-shadow: 0 4px 14px rgba(12, 35, 63, 0.08);
  border-color: #d8e0ea;
}

.uni-card--appt-approved,
.uni-card--appt-done {
  border-left-color: var(--agenda-success);
  background: linear-gradient(90deg, var(--agenda-success-soft) 0%, #fff 55%);
}

.uni-card--appt-pending,
.uni-card--appt-pending-payment,
.uni-card--appt-approved-unpaid {
  border-left-color: var(--agenda-warn);
  background: linear-gradient(90deg, var(--agenda-warn-soft) 0%, #fff 55%);
}

.uni-card--event {
  border-left: 3px solid transparent;
}

.uni-card--event-webinar {
  border-left-color: var(--agenda-violet);
  background: linear-gradient(90deg, var(--agenda-violet-soft) 0%, #fff 50%);
}

.uni-card--event-webinar.uni-card--webinar-registered {
  border-left-color: var(--agenda-success);
  background: linear-gradient(90deg, var(--agenda-success-soft) 0%, #fff 55%);
}

.uni-card--event-public {
  border-left-color: var(--agenda-accent);
  background: linear-gradient(90deg, var(--agenda-accent-soft) 0%, #fff 50%);
}

.uni-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.uni-card__head-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}

.uni-card__time {
  font-size: 0.76rem;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  background: var(--agenda-surface);
  color: var(--agenda-primary);
  white-space: nowrap;
}

.uni-card__time--state {
  background: var(--agenda-accent-soft);
  color: var(--agenda-accent-dark);
}

.uni-card__open {
  color: #b0bac7;
  font-size: 0.85rem;
  margin-top: 0.15rem;
}

.uni-card__title {
  margin: 0 0 0.35rem;
  font-size: 0.94rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--agenda-primary);
}

.uni-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
}

.uni-card__status {
  font-weight: 600;
}

.uni-card__status--upcoming {
  color: var(--agenda-accent-dark);
  font-weight: 500;
}

.uni-card__status--approved {
  color: var(--agenda-success);
}

.uni-card__status--pending,
.uni-card__status--pending-payment,
.uni-card__status--approved-unpaid {
  color: var(--agenda-warn);
}

.uni-card__meta-text {
  color: var(--agenda-muted);
}

.origin-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.5rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  border: none;
}

.origin-tag--appointment {
  background: var(--agenda-accent-soft);
  color: var(--agenda-accent-dark);
}

.origin-tag--webinar {
  background: var(--agenda-violet-soft);
  color: var(--agenda-violet);
}

.origin-tag--public {
  background: var(--agenda-success-soft);
  color: var(--agenda-success);
}

.origin-tag--conference {
  background: #fff1f2;
  color: #be123c;
}

.uni-external-link {
  color: var(--agenda-muted);
  font-size: 0.85rem;
  transition: color 0.15s ease;
}

.uni-external-link:hover {
  color: var(--agenda-accent);
}

.uni-card__register-btn.p-button {
  white-space: nowrap;
  font-size: 0.75rem !important;
  padding: 0.35rem 0.75rem !important;
  border-radius: 6px !important;
  background: var(--agenda-accent) !important;
  border-color: var(--agenda-accent) !important;
  color: #fff !important;
}

.uni-card__register-btn.p-button:hover {
  background: var(--agenda-accent-dark) !important;
  border-color: var(--agenda-accent-dark) !important;
}

.uni-card__cancel-btn.p-button,
.uni-card__cancel-btn.p-button:enabled:hover,
.uni-card__cancel-btn.p-button:enabled:active,
.uni-card__cancel-btn.p-button:enabled:focus {
  white-space: nowrap;
  font-size: 0.75rem !important;
  padding: 0.2rem 0.45rem !important;
  color: var(--agenda-muted) !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.uni-card__cancel-btn.p-button:enabled:hover {
  color: var(--agenda-primary) !important;
  background: var(--agenda-surface) !important;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.uni-card__cancel-btn.p-button:enabled:active {
  color: var(--agenda-accent-dark) !important;
  background: var(--agenda-accent-soft) !important;
  text-decoration: none;
}

.dashboard-agenda-hub__footer {
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--agenda-border);
}

.dashboard-agenda-hub__empty {
  padding-top: 0.5rem;
}
</style>
